import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Check, Sparkles, ArrowRight, Loader2, ChevronLeft, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  "Unlimited newsletters",
  "Unlimited contacts",
  "Unlimited campaigns",
  "AI content generation (GPT-4o)",
  "All 13 themes — general + industry",
  "3 layouts: Magazine · Story · Cards",
  "Smart scheduling",
  "Opens, clicks, bounces analytics",
  "CSV / Excel contact import",
  "Email support",
  "Cancel anytime",
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { token, authAxios } = useAuth();
  const [spots, setSpots] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null); // 'standard_monthly' | 'founder_monthly' | null

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/billing/founder-spots-remaining`)
      .then(r => setSpots(r.data))
      .catch(() => setSpots({ used: 0, remaining: 100, cap: 100 }));
  }, []);

  const startCheckout = async (plan) => {
    if (!token) {
      // Send unauthenticated visitors to sign up first, remember which plan they picked
      sessionStorage.setItem("sse_pending_plan", plan);
      navigate("/login?signup=1&next=/pricing");
      return;
    }
    setLoadingPlan(plan);
    try {
      const res = await authAxios.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const detail = e.response?.data?.detail || "Couldn't start checkout — please try again";
      toast.error(detail);
      setLoadingPlan(null);
    }
  };

  // Resume checkout if user signed up just now
  useEffect(() => {
    if (token) {
      const pending = sessionStorage.getItem("sse_pending_plan");
      if (pending) {
        sessionStorage.removeItem("sse_pending_plan");
        startCheckout(pending);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const founderRemaining = spots?.remaining ?? 100;
  const founderClosed = founderRemaining === 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003858] via-[#00466e] to-[#119AB3] text-white" data-testid="pricing-page">
      <nav className="sticky top-0 z-50 bg-[#003858]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={() => navigate(token ? "/dashboard" : "/")} className="flex items-center text-sm text-white/70 hover:text-white" data-testid="pricing-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <div className="flex items-center gap-2 text-sm font-semibold tracking-wide" style={{fontFamily: "Manrope"}}>
            <Sparkles className="w-4 h-4 text-[#B7EB1B]" /> Simply Smarter Email
          </div>
          <div className="w-12" />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20 text-center">
        <Badge className="bg-[#B7EB1B] text-[#0a1e3a] font-bold tracking-wide px-3 py-1.5 mb-5">14-DAY FREE TRIAL · NO CREDIT CARD</Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4" style={{fontFamily: "Manrope"}}>
          Email marketing, made simple.
        </h1>
        <p className="text-lg text-white/80 max-w-2xl mx-auto">
          AI drafts a beautiful starting point. You customize every word and every photo. Nothing sends until you say so.
        </p>
      </div>

      {/* Pricing cards */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Founding Member */}
          <div
            className={`relative rounded-3xl p-8 ${
              founderClosed
                ? "bg-white/5 border border-white/10 opacity-60"
                : "bg-white text-slate-900 shadow-2xl border-2 border-[#B7EB1B]"
            }`}
            data-testid="pricing-card-founder"
          >
            {!founderClosed && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B7EB1B] text-[#0a1e3a] text-xs font-extrabold tracking-wider px-3 py-1 rounded-full uppercase">
                Limited · First 100
              </div>
            )}
            <div className="text-xs font-semibold tracking-wide uppercase text-[#0d9488] mb-2">Founding Member</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-extrabold" style={{fontFamily: "Manrope"}}>$49</span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="text-sm font-semibold text-[#0d9488] mb-6">Locked in for life of your subscription.</p>

            <div className={`text-sm mb-6 ${founderClosed ? "text-white/80" : "text-slate-700"}`}>
              {spots
                ? founderClosed
                  ? "All 100 founder spots are claimed."
                  : <><span className="font-bold text-[#0d9488]">{founderRemaining}</span> of {spots.cap} spots remaining</>
                : "Loading availability…"}
            </div>

            <Button
              data-testid="pricing-founder-cta"
              disabled={founderClosed || loadingPlan === "founder_monthly"}
              onClick={() => startCheckout("founder_monthly")}
              className="w-full bg-[#0d9488] hover:bg-[#0d9488]/90 text-white rounded-full font-bold h-12"
            >
              {loadingPlan === "founder_monthly" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
                : founderClosed ? "Spots full"
                : <>Claim Founder Spot <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
            <p className="text-xs text-slate-400 mt-3 text-center">14-day free trial · No credit card needed</p>
          </div>

          {/* Standard */}
          <div className="rounded-3xl p-8 bg-white text-slate-900 shadow-2xl border border-slate-200" data-testid="pricing-card-standard">
            <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2">Standard</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-extrabold" style={{fontFamily: "Manrope"}}>$79</span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="text-sm text-slate-500 mb-6">No commitment. Cancel anytime.</p>

            <div className="text-sm text-slate-700 mb-6">
              Same features as Founder. The whole product, every month.
            </div>

            <Button
              data-testid="pricing-standard-cta"
              disabled={loadingPlan === "standard_monthly"}
              onClick={() => startCheckout("standard_monthly")}
              className="w-full bg-[#003858] hover:bg-[#003858]/90 text-white rounded-full font-bold h-12"
            >
              {loadingPlan === "standard_monthly"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
                : <>Start 14-day Free Trial <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
            <p className="text-xs text-slate-400 mt-3 text-center">14-day free trial · No credit card needed</p>
          </div>
        </div>

        {/* Features (shared) */}
        <div className="mt-12 rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold mb-1" style={{fontFamily: "Manrope"}}>Everyone gets everything</h3>
            <p className="text-white/60 text-sm">Same product, same features, same support — no upsells inside.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            {FEATURES.map(f => (
              <div key={f} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#B7EB1B] flex-shrink-0 mt-0.5" />
                <span className="text-white/90">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trust */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/60">
          <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#B7EB1B]" /> Stripe-secured checkout</span>
          <span>·</span>
          <span>Cancel from your dashboard anytime</span>
          <span>·</span>
          <span>14 actual days · no surprise charges</span>
        </div>
      </div>
    </div>
  );
}
